/**
 * 弹窗内 hidden file input 辅助（iOS / Web +Upload 主路径，Android session 时 onChange 需跳过）
 *
 * - `buildPortfolioPdfPickPreset`：与 `portfolioPickConfig` PDF preset 对齐
 * - `createPortfolioFileInputChangeHandler`：Sender 式 onChange → prepare → handleFilesSelected
 */
import type React from "react";
import type { PortfolioUploadType } from "@components/PortfolioScreenshotModal/types";
import { type PrepareFileInputParams } from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";
import { preparePortfolioUploadFiles } from "../../upload/prepareUploadFiles";
import { PORTFOLIO_PDF_ACCEPT } from "../../constants/uploadConstants";

/** PDF 文档选择器 preset（iOS prepareFileInput mode: document） */
export function buildPortfolioPdfPickPreset(): PrepareFileInputParams {
  return {
    mode: "document",
    accept: PORTFOLIO_PDF_ACCEPT,
    capture: null,
    multiple: true,
  };
}

type HandleFilesSelected = (
  selected: File[],
  options?: { skipPrepare?: boolean },
) => Promise<boolean>;

/**
 * 生成与 Sender 对齐的 onChange 处理器。
 * in-app 下先 `preparePortfolioUploadFiles` 再上传（skipPrepare 避免重复克隆）。
 */
export function createPortfolioFileInputChangeHandler(
  inputRef: React.RefObject<HTMLInputElement | null>,
  uploadType: PortfolioUploadType,
  handleFilesSelected: HandleFilesSelected,
): (event: React.ChangeEvent<HTMLInputElement>) => Promise<void> {
  return async (event) => {
    const picked = Array.from(event.target.files || []);
    if (!picked.length) {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    const bridge = getBridge();
    let prepared = picked;
    if (bridge.isInApp()) {
      prepared = await preparePortfolioUploadFiles(uploadType, picked);
      if (!prepared.length) {
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }

    await handleFilesSelected(prepared, {
      skipPrepare: bridge.isInApp(),
    });
  };
}
